import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Users, Plus, Search, Download, Upload,
  UserCheck, UserX, UserCog, RefreshCw,
} from 'lucide-react'
import {
  useClientes,
  useClienteStats,
  useEliminarCliente,
  useExportarClientes,
  usePaises,
  useCargos,
  useLineas,
  useLaboratorios,
} from '../hooks/useBdClientes'
import { ClientesTable } from '../components/ClientesTable'
import { StatsCard } from '../components/StatsCard'
import type { FiltrosClientes } from '../types'

export function ClientesListPage() {
  useDocumentTitle('BD Clientes - Seguimiento')
  const navigate = useNavigate()

  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [filtros, setFiltros] = useState<FiltrosClientes>({ page: 1, page_size: 25 })

  const { data, isLoading } = useClientes(filtros)
  const { data: stats, isLoading: statsLoading } = useClienteStats()
  const eliminarMutation = useEliminarCliente()
  const exportarMutation = useExportarClientes()

  // Config para filtros
  const { data: paises } = usePaises(true)
  const { data: cargos } = useCargos(undefined, true)
  const { data: lineas } = useLineas(true)
  const { data: laboratorios } = useLaboratorios(filtros.pais_id)

  const handleSearch = () => {
    setFiltros(prev => ({ ...prev, search: searchInput || undefined, page: 1 }))
    setPage(1)
  }

  const handleFilterChange = (key: string, value: string) => {
    setFiltros(prev => ({
      ...prev,
      [key]: value === 'all' ? undefined : (key.endsWith('_id') ? Number(value) : value),
      page: 1,
    }))
    setPage(1)
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
    setFiltros(prev => ({ ...prev, page: newPage }))
  }

  const handleDelete = async (id: number) => {
    if (confirm('¿Estás seguro de eliminar este cliente? Esta acción no se puede deshacer.')) {
      await eliminarMutation.mutateAsync(id)
    }
  }

  const handleExport = () => {
    exportarMutation.mutate(filtros)
  }

  const clearFilters = () => {
    setSearchInput('')
    setFiltros({ page: 1, page_size: 25 })
    setPage(1)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Users className="h-6 w-6 text-teal-500" />
          BD Clientes - Seguimiento
        </h1>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/bd-clientes/carga-masiva')}
            className="border-gray-700 text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <Upload className="mr-2 h-4 w-4" />
            Carga Masiva
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exportarMutation.isPending}
            className="border-gray-700 text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <Download className="mr-2 h-4 w-4" />
            Exportar
          </Button>
          <Button
            size="sm"
            onClick={() => navigate('/bd-clientes/crear')}
            className="bg-teal-800 hover:bg-teal-900 text-white"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Cliente
          </Button>
        </div>
      </div>

      {/* Stats */}
      {statsLoading ? (
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <StatsCard title="Total Clientes" value={stats.total} icon={Users} />
          <StatsCard title="Activos" value={stats.activos} icon={UserCheck} />
          <StatsCard title="Inactivos" value={stats.inactivos} icon={UserX} />
          <StatsCard title="Por Validar" value={stats.prospectos} icon={UserCog} />
        </div>
      )}

      {/* Filtros */}
      <Card className="p-4 border border-gray-300 dark:border-gray-700">
        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nombre, email, teléfono, marca..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button variant="outline" onClick={handleSearch}>
              <Search className="h-4 w-4" />
            </Button>
            <Button variant="ghost" onClick={clearFilters} title="Limpiar filtros">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Select
              value={filtros.pais_id?.toString() || 'all'}
              onValueChange={(v) => {
                handleFilterChange('pais_id', v)
                // Reset laboratorio al cambiar país
                setFiltros(prev => ({ ...prev, laboratorio_id: undefined }))
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="País" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los países</SelectItem>
                {paises?.map((p) => (
                  <SelectItem key={p.id} value={p.id.toString()}>{p.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filtros.laboratorio_id?.toString() || 'all'}
              onValueChange={(v) => handleFilterChange('laboratorio_id', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Laboratorio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los laboratorios</SelectItem>
                {laboratorios?.map((l) => (
                  <SelectItem key={l.id} value={l.id.toString()}>{l.razon_social}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filtros.cargo_id?.toString() || 'all'}
              onValueChange={(v) => handleFilterChange('cargo_id', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Cargo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los cargos</SelectItem>
                {cargos?.map((c) => (
                  <SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filtros.linea_id?.toString() || 'all'}
              onValueChange={(v) => handleFilterChange('linea_id', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Línea" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las líneas</SelectItem>
                {lineas?.map((l) => (
                  <SelectItem key={l.id} value={l.id.toString()}>{l.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filtros.estado || 'all'}
              onValueChange={(v) => handleFilterChange('estado', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="activo">Activo</SelectItem>
                <SelectItem value="inactivo">Inactivo</SelectItem>
                <SelectItem value="prospecto">Por validar</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Tabla */}
      <Card className="border border-gray-300 dark:border-gray-700">
        {isLoading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
          </div>
        ) : (
          <>
            <ClientesTable
              clientes={data?.results || []}
              onDelete={handleDelete}
            />

            {/* Paginación */}
            {data && data.total_pages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-gray-300 dark:border-gray-700">
                <p className="text-sm text-muted-foreground">
                  {data.count} clientes encontrados
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                  >
                    Anterior
                  </Button>
                  <span className="flex items-center text-sm px-2">
                    Página {page} de {data.total_pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === data.total_pages}
                    onClick={() => handlePageChange(page + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  )
}
